import { createNavigation } from "next-intl/navigation";
import { defineRouting } from "next-intl/routing";
import campaignsPagePath from "../app/[locale]/(site)/(editorial)/campaigns/[[...slug]]/_routes.json";
import explorePagePath from "../app/[locale]/(site)/(editorial)/explore/[[...slug]]/_routes.json";
import guidesPagePath from "../app/[locale]/(site)/(editorial)/guides/[[...slug]]/_routes.json";
import eccoKollektivePage from "../app/[locale]/(site)/(editorial)/ecco-kollektive/[[...slug]]/_routes.json";
import storiesPagePath from "../app/[locale]/(site)/(editorial)/stories/[[...slug]]/_routes.json";
import orderPagePath from "../app/[locale]/(site)/(order)/order/_routes.json";
import orderConfirmationPagePath from "../app/[locale]/(site)/(order)/order/confirmation/_routes.json";
import checkoutPagePath from "../app/[locale]/(custom)/checkout/_routes.json";
import checkoutV2PagePath from "../app/[locale]/(custom)/checkoutv2/_routes.json";
import topLevelPagePath from "../app/_routes.json";
import createAccountPagePath from "../app/[locale]/(site)/(account)/create-account/_routes.json";
import verifyPagePath from "../app/[locale]/(site)/(account)/create-account/verify/_routes.json";
import productPagePath from "../app/[locale]/(site)/product/_routes.json";
import orderTrackingPagePath from "../app/[locale]/(site)/(order)/order-tracking/_routes.json";
import myAccountPagePath from "../app/[locale]/(site)/(account)/my-account/_routes.json";
import customerServicePagePath from "../app/[locale]/(site)/customer-service/[...slug]/_routes.json";
import customerServiceLegalPages from "../app/[locale]/(site)/customer-service/legal-pages/[...slug]/_routes.json";
import bagsAndAccesoriesPagePath from "../app/[locale]/(site)/(listing)/bags-and-accessories/_routes.json";
import blackFridayPagePath from "../app/[locale]/(site)/(listing)/black-friday/_routes.json";
import collectionsPagePath from "../app/[locale]/(site)/(listing)/collections/_routes.json";
import cyberMondayPagePath from "../app/[locale]/(site)/(listing)/cyber-monday/_routes.json";
import eccoKollektiveCollectionsPagePath from "../app/[locale]/(site)/(listing)/ecco-kollektive-collections/_routes.json";
import golfPagePath from "../app/[locale]/(site)/(listing)/golf/_routes.json";
import kidsPagePath from "../app/[locale]/(site)/(listing)/kids/_routes.json";
import menPagePath from "../app/[locale]/(site)/(listing)/men/_routes.json";
import newPagePath from "../app/[locale]/(site)/(listing)/new/_routes.json";
import outdoorPagePath from "../app/[locale]/(site)/(listing)/outdoor/_routes.json";
import salePagePath from "../app/[locale]/(site)/(listing)/sale/_routes.json";
import shoeCarePagePath from "../app/[locale]/(site)/(listing)/shoe-care/_routes.json";
import womenPagePath from "../app/[locale]/(site)/(listing)/women/_routes.json";
import addressesPagePath from "../app/[locale]/(site)/(account)/my-account/addresses/_routes.json";
import eccoClubPagePath from "../app/[locale]/(site)/(account)/my-account/ecco-club/_routes.json";
import ordersPagePath from "../app/[locale]/(site)/(account)/my-account/orders/_routes.json";
import profilePagePath from "../app/[locale]/(site)/(account)/my-account/profile/_routes.json";
import subscriptionsPagePath from "../app/[locale]/(site)/(account)/my-account/subscriptions/_routes.json";
import wishlistPagePath from "../app/[locale]/(site)/(account)/my-account/wishlist/_routes.json";

const rawLocales = process.env.NEXT_PUBLIC_LOCALES?.split(",") ?? [];
if (rawLocales.length === 0) {
    throw new Error("NEXT_PUBLIC_LOCALES environment variable is not set or empty.");
}

const systemRoutes = [
    checkoutPagePath["/"],
    checkoutV2PagePath["/"],
    orderPagePath["/"],
    topLevelPagePath["shopping-bag"],
    topLevelPagePath["store-locator"],
    orderTrackingPagePath["/"],
    productPagePath["/"],
    myAccountPagePath["/"],
    topLevelPagePath["search"],
    topLevelPagePath["log-in"],
    createAccountPagePath["/"],
    topLevelPagePath["reset-password"],
    topLevelPagePath["unsubscribe"],
    customerServicePagePath["/"],
    bagsAndAccesoriesPagePath["/"],
    blackFridayPagePath["/"],
    collectionsPagePath["/"],
    cyberMondayPagePath["/"],
    eccoKollektiveCollectionsPagePath["/"],
    golfPagePath["/"],
    kidsPagePath["/"],
    menPagePath["/"],
    newPagePath["/"],
    outdoorPagePath["/"],
    salePagePath["/"],
    shoeCarePagePath["/"],
    womenPagePath["/"],
];

const editorialRoutes = [
    { key: "stories", path: storiesPagePath },
    { key: "guides", path: guidesPagePath },
    { key: "explore", path: explorePagePath },
    { key: "campaigns", path: campaignsPagePath },
    { key: "ecco-kollektive", path: eccoKollektivePage },
    { key: "customer-service", path: customerServicePagePath },
];

const myAccountPagesList = [
    { key: "addresses", path: addressesPagePath },
    { key: "ecco-club", path: eccoClubPagePath },
    { key: "orders", path: ordersPagePath },
    { key: "profile", path: profilePagePath },
    { key: "subscriptions", path: subscriptionsPagePath },
    { key: "wishlist", path: wishlistPagePath },
];

const categoryRoutes = [
    { key: "bags-and-accessories", path: bagsAndAccesoriesPagePath },
    { key: "black-friday", path: blackFridayPagePath },
    { key: "collections", path: collectionsPagePath },
    { key: "cyber-monday", path: cyberMondayPagePath },
    { key: "ecco-kollektive-collections", path: eccoKollektiveCollectionsPagePath },
    { key: "golf", path: golfPagePath },
    { key: "kids", path: kidsPagePath },
    { key: "men", path: menPagePath },
    { key: "new", path: newPagePath },
    { key: "outdoor", path: outdoorPagePath },
    { key: "sale", path: salePagePath },
    { key: "shoe-care", path: shoeCarePagePath },
    { key: "women", path: womenPagePath },
];

export type SupportedLocale = keyof (typeof systemRoutes)[number];

const allValidLocales = Object.keys(systemRoutes[0]) as SupportedLocale[];

const isValidLocale = (locale: string): locale is SupportedLocale => {
    return allValidLocales.includes(locale as SupportedLocale);
};

const locales = rawLocales.filter(isValidLocale);
if (locales.length === 0) {
    throw new Error(
        `NEXT_PUBLIC_LOCALES must include at least one of: ${allValidLocales.join(", ")}`
    );
}
const defaultLocale = locales[0];

type LocalePaths = Record<SupportedLocale, string>;

type Pathnames = {
    [path: string]: LocalePaths;
};

const generatePathnames = (locales: SupportedLocale[]): Pathnames => {
    const pathnames: Pathnames = {};
    const addRoute = (locale: SupportedLocale, key: string, routePath: string) => {
        const decodedKey = decodeURIComponent(key);

        if (!pathnames[decodedKey]) {
            pathnames[decodedKey] = {} as Record<SupportedLocale, string>;
        }

        pathnames[decodedKey][locale] = decodeURIComponent(routePath);
    };

    for (const locale of locales) {
        for (const route of systemRoutes) {
            if (route[locale] != null) {
                addRoute(locale, `/${route["en"]}`, `/${route[locale]}`);
            }
        }

        editorialRoutes.forEach(({ path }) => {
            const defaultBase = path["/"]["en"];

            const baseKey = `/${defaultBase}/[[...slug]]`;

            const localizedBase = path["/"]?.[locale];
            const localizedRoute = localizedBase
                ? `/${localizedBase}/[[...slug]]`
                : `/${defaultBase}/[[...slug]]`;

            addRoute(locale, baseKey, localizedRoute);
        });

        const orderConfirmationLocalizedRoute = `/${orderPagePath["/"][locale]}/${orderConfirmationPagePath["/"][locale]}/[id]`;
        const orderConfirmationRoute = `/${orderPagePath["/"]["en"]}/${orderConfirmationPagePath["/"]["en"]}/[id]`;
        addRoute(locale, orderConfirmationRoute, orderConfirmationLocalizedRoute);

        const orderConfirmationV2LocalizedRoute = `/${orderPagePath["/"][locale]}/${orderConfirmationPagePath["/"][locale]}/v2/[id]`;
        const orderConfirmationV2Route = `/${orderPagePath["/"]["en"]}/${orderConfirmationPagePath["/"]["en"]}/v2/[id]`;
        addRoute(locale, orderConfirmationV2Route, orderConfirmationV2LocalizedRoute);

        const legalLocalizedRoute = `/${customerServicePagePath["/"][locale]}/${customerServiceLegalPages["/"][locale]}/[...slug]`;
        const legalRoute = `/${customerServicePagePath["/"]["en"]}/${customerServiceLegalPages["/"]["en"]}/[...slug]`;
        addRoute(locale, legalRoute, legalLocalizedRoute);

        const productRoute = `/${productPagePath["/"]["en"]}/[productName]/[articleNumber]/[colorCode]`;
        const productLocalizedRoute = `/${productPagePath["/"][locale]}/[productName]/[articleNumber]/[colorCode]`;
        addRoute(locale, productRoute, productLocalizedRoute);

        const createAccountVerifyLocalizedRoute = `/${createAccountPagePath["/"][locale]}/${verifyPagePath["/"][locale]}`;
        const createAccountVerifyRoute = `/${createAccountPagePath["/"]["en"]}/${verifyPagePath["/"]["en"]}`;
        addRoute(locale, createAccountVerifyRoute, createAccountVerifyLocalizedRoute);

        for (const page of myAccountPagesList) {
            const localizedRoute = `/${myAccountPagePath["/"][locale]}/${page.path["/"][locale]}`;
            const route = `/${myAccountPagePath["/"]["en"]}/${page.path["/"]["en"]}`;
            addRoute(locale, route, localizedRoute);
        }

        categoryRoutes.forEach(({ path }) => {
            const defaultBase = path["/"]["en"];
            const baseKey = `/${defaultBase}/[...categories]`;
            const localizedBase = path["/"]?.[locale];
            const localizedRoute = localizedBase
                ? `/${localizedBase}/[...categories]`
                : `/${defaultBase}/[...categories]`;

            addRoute(locale, baseKey, localizedRoute);
        });
    }

    return pathnames;
};

export const routing = defineRouting({
    locales: locales,
    defaultLocale: defaultLocale,
    alternateLinks: false,
    localePrefix: locales.length > 1 ? "as-needed" : "never",
    localeDetection: false,
    pathnames: generatePathnames(locales),
});

export const pathnames = routing.pathnames;

export const { Link, redirect, usePathname, useRouter, getPathname, permanentRedirect } =
    createNavigation(routing);
